/**
 * One card's worth of review state: the typed recall, whether the card is turned over, the
 * confidence that turned it, focus and the keyboard. Mounted with the card's key, so every card
 * starts fresh with nothing to reset.
 *
 * Keyboard (pedagogy spec §6.3.4), all through `shortcutsBlocked` so typing is never hijacked:
 *   - in the input, Enter moves focus to the confidence group container, never to a button, and
 *     Escape blurs it;
 *   - on the front, 1-3 choose Osäker, Ganska säker or Helt säker once something is typed, which
 *     turns the card over; focus then goes to the container of the back and the rating buttons;
 *   - after the flip, 1-4 rate. A rating within 400 ms of the flip, or from a held key, is
 *     ignored, so the digit that chose a confidence cannot rate the card as well.
 */
import { useEffect, useRef, useState } from "react";
import type { Rating } from "@shared/sm2";
import { shortcutsBlocked } from "@client/lib/keyboard";
import { CONFIDENCE_KEYS, RATING_KEYS, ReviewCard } from "./ReviewCard";
import type { SessionCard } from "./session";
import type { RecallConfidence } from "./types";

/** A rating this soon after the flip is a stray repeat of the key that flipped it. */
export const RATE_GUARD_MS = 400;

/** Autofocusing the input would pop up the keyboard on a phone. */
function hasFinePointer(): boolean {
  try {
    return window.matchMedia("(pointer: fine)").matches;
  } catch {
    return false;
  }
}

export interface ReviewStepProps {
  card: SessionCard;
  /** A review request is in flight. */
  busy: boolean;
  /** `confidence` is the level the buttons stand for (2, 3, 4), null for "Jag vet inte". */
  onRate: (rating: Rating, confidence: RecallConfidence | null) => void;
}

export function ReviewStep({ card, busy, onRate }: ReviewStepProps) {
  const [recall, setRecall] = useState("");
  const [flipped, setFlipped] = useState(false);
  const [confidence, setConfidence] = useState<RecallConfidence | null>(null);
  const flippedAt = useRef(0);
  const frontRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const confidenceRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);

  function choose(level: RecallConfidence | null) {
    if (flipped) return;
    flippedAt.current = Date.now();
    setConfidence(level);
    setFlipped(true);
  }

  function rate(rating: Rating) {
    if (!flipped || busy) return;
    if (Date.now() - flippedAt.current < RATE_GUARD_MS) return;
    onRate(rating, confidence);
  }

  const latest = useRef({ choose, rate, flipped });
  latest.current = { choose, rate, flipped };

  // A new card takes focus: the input where there is a hardware keyboard, otherwise the front,
  // so focus never falls to <body> when the rating buttons of the last card go away.
  useEffect(() => {
    if (hasFinePointer()) inputRef.current?.focus();
    else frontRef.current?.focus();
  }, []);
  useEffect(() => {
    if (flipped) revealRef.current?.focus();
  }, [flipped]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (shortcutsBlocked(event) || event.repeat) return;
      if (!latest.current.flipped) {
        const level = CONFIDENCE_KEYS[event.key];
        if (level) latest.current.choose(level);
        return;
      }
      const rating = RATING_KEYS[event.key];
      if (rating) latest.current.rate(rating);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <ReviewCard
      card={card}
      flipped={flipped}
      recall={recall}
      onRecall={setRecall}
      onChoose={choose}
      onRate={rate}
      rating={busy}
      frontRef={frontRef}
      inputRef={inputRef}
      confidenceRef={confidenceRef}
      revealRef={revealRef}
    />
  );
}
